import React, { useMemo, useRef } from 'react';
import { DirectionalHint, getTheme, Icon } from '@fluentui/react';
import { INTERNAL__getAppHeaderStyles } from './style';
import { AppLogo } from '../app-logo';
import { AppHeaderButton } from '../app-header-button';
import { EventNotificationService, useTransientStatefulEventNotification } from '../../framework/services/event-notification';
import { ShopAppEvents } from '../../app-shop/events';
import { isEmptyString } from '../../framework/utils/string';
import { BwjTutorialPoint } from '../../framework/components/bwj-tutorial-point';
import { TutorialEvents, TutorialService } from '../../app-shop/services/tutorial';
import { isTouchDevice } from '../../framework/utils/device';

export interface IAppHeaderProps {
  showMenu: boolean;
  showCart: boolean;
  showCustomerAccount: boolean;
  homeowner?: string | undefined;
  logoHref?: string;
  onDesktopMenuButtonClick: () => void;
  onMobileMenuButtonClick: () => void;
}

const mobileDevice = isTouchDevice();
const subscriberId = 'Component.AppHeader';

export const AppHeader: React.FunctionComponent<IAppHeaderProps> = (props) => {
  const theme = getTheme();
  const classNames = INTERNAL__getAppHeaderStyles(theme);
  const { homeowner } = props;
  const mobileButtonElement = useRef<HTMLButtonElement | null>(null);
  const shoppingCartElement = useRef<HTMLDivElement | null>(null);
  const showCartTutorialArgs = useTransientStatefulEventNotification<boolean>(TutorialEvents.TeachShoppingCart, subscriberId);
  const sta = useTransientStatefulEventNotification<boolean>(TutorialEvents.TeachShoppingPreferencesMenu, subscriberId);
  const showPrefsTutorialArgs = useMemo(() => {
    if(!mobileButtonElement.current) { return undefined; }
    const onDisplay = window.getComputedStyle(mobileButtonElement.current).display !== 'none';
    return onDisplay ? sta : undefined;
  }, [sta]);

  const subtitle = useMemo(() => {
    if(isEmptyString(homeowner)) { return null; }
    const ho = homeowner!.trim();
    const lastLetter = ho[ho.length - 1];
    return lastLetter === 's' ? `@ ${ho}'` : `@ ${ho}'s`;
  }, [homeowner]);

  const onShoppingCartClick = () => {
    onShoppingCartLessonTaught();
    EventNotificationService.dispatch(ShopAppEvents.ShowShoppingCartPanel);
  }

  const onProjectAccountClick = () => {
    EventNotificationService.dispatch(ShopAppEvents.ShowProjectAccountPanel);
  }

  const onPreferenceMenuLessonTaught = () => {
    TutorialService.markShoppingPreferencesMenuTaught();
  }

  const onShoppingCartLessonTaught = () => {
    TutorialService.markShoppingCartTaught();
  }

  const onMobileMenuButtonClick = () => {
    onPreferenceMenuLessonTaught();
    props.onMobileMenuButtonClick();
  }

  return (
    <header className={classNames.header}>
      <div className={classNames.headerMain}>
        <div className={classNames.leftContainer}>
          {props.showMenu && <>
          <button
            className={classNames.desktopMenuButton}
            onClick={props.onDesktopMenuButtonClick}
          >
            <Icon iconName='GlobalNavButton' />
          </button>
          <button
            className={classNames.mobileMenuButton}
            onClick={onMobileMenuButtonClick}
            ref={mobileButtonElement}
          >
            <Icon iconName='GlobalNavButton' />
          </button>
          <BwjTutorialPoint
            id={TutorialEvents.TeachShoppingPreferencesMenu}
            hostElement={mobileButtonElement.current}
            position={DirectionalHint.bottomLeftEdge}
            showTutorialArgs={showPrefsTutorialArgs}
            title={'Shop Your Way'}
            textContent={'Tap the menu button above to use your shopping preferences panel.  There, you can specify the door colors you wish to choose from and the order in which your choices appear.'}
            primaryButton={{
              icon: 'Like',
              text: 'Got it',
              onClick: onPreferenceMenuLessonTaught,
            }}
            onDismissed={onPreferenceMenuLessonTaught}
          />
          </>}
          <h1>
            <AppLogo business='Storm' href={props.logoHref} />
          </h1>
          {!!subtitle &&
          <p className={classNames.subtitleDesktop}>
            {subtitle}
            <Icon iconName='HomeSolid' title='Home' />
          </p>}
        </div>
        <ul className={classNames.headerMenu}>
          {props.showCart && <>
          <div ref={shoppingCartElement}>
            <AppHeaderButton
              icon='ShoppingCart'
              ariaDescription='My Cart'
              onClick={onShoppingCartClick}
            />
          </div>
          <BwjTutorialPoint
            id={TutorialEvents.TeachShoppingCart}
            hostElement={shoppingCartElement.current}
            position={DirectionalHint.bottomRightEdge}
            showTutorialArgs={showCartTutorialArgs}
            title={'Your Project'}
            textContent={`So far so good! You can ${mobileDevice ? 'tap' : 'click'} the cart button above whenever you want to review or change your selections for this project.`}
            primaryButton={{
              icon: 'Like',
              text: 'Got it',
              onClick: onShoppingCartLessonTaught,
            }}
            onDismissed={onShoppingCartLessonTaught}
          />
          </>}
          {props.showCustomerAccount &&
          <AppHeaderButton
            icon='ContactInfo'
            ariaDescription='My Project Account'
            onClick={onProjectAccountClick}
          />}
        </ul>
      </div>
      {!!subtitle &&
      <p className={classNames.subtitleMobile}>
        {subtitle}
        <Icon iconName='HomeSolid' title='Home' />
      </p>}
    </header>
  );
};